import { apiFetch } from './client'

export type ChatStatus = 'running' | 'idle' | 'aborted' | 'error'
export type ChatMessageRole = 'user' | 'agent' | 'system' | 'tool'

export interface ChatConversationDto {
  id: string
  channel: 'telegram' | 'web'
  name: string
  handle: string
  initials: string
  agent: string
  project: string
  sprint: string | null
  engine: string
  model: string
  preview: string
  time: string
  unread: number
  running: boolean
}

export interface ChatMessageDto {
  id: string
  sessionId: string
  role: ChatMessageRole
  author: string
  text: string
  time: string
  status?: 'done' | 'running' | 'blocked'
  toolName?: string
}

export interface ChatSessionDto {
  id: string
  agent: string
  project: string
  sprint: string | null
  engine: string
  model: string
  mode: string
  status: ChatStatus
  startedAt: string
  lastActivityAt: string
  lastError?: string
  runId?: string
}

export interface ChatSendDto {
  session: ChatSessionDto
  userMessage: ChatMessageDto
  runId: string
  status: 'running'
}

export type ChatStreamEvent =
  | { type: 'ready'; sessionId: string; runId: string }
  | { type: 'agent_text'; sessionId: string; runId: string; message: ChatMessageDto }
  | { type: 'done'; sessionId: string; runId: string; session: ChatSessionDto }
  | { type: 'error'; sessionId: string; runId: string; message: string; session: ChatSessionDto }
  | { type: 'aborted'; sessionId: string; runId: string; session: ChatSessionDto }

export function fetchChatConversations(): Promise<ChatConversationDto[]> {
  return apiFetch<ChatConversationDto[]>('/api/chat/conversations')
}

export function createChatSession(input: {
  agent: string
  project?: string
  sprint?: string | null
  mode?: string
}): Promise<ChatSessionDto> {
  return apiFetch<ChatSessionDto>('/api/chat/sessions', {
    method: 'POST',
    json: input,
  })
}

export function fetchChatMessages(agentId: string, sessionId?: string | null): Promise<ChatMessageDto[]> {
  return apiFetch<ChatMessageDto[]>(`/api/chat/${encodeURIComponent(agentId)}/messages`, {
    query: { sessionId },
  })
}

export function sendChatMessage(agentId: string, sessionId: string, text: string): Promise<ChatSendDto> {
  return apiFetch<ChatSendDto>(`/api/chat/${encodeURIComponent(agentId)}/messages`, {
    method: 'POST',
    json: { sessionId, text },
  })
}

export function sendChatCommand(agentId: string, sessionId: string, cmd: string, args = ''): Promise<ChatSendDto> {
  return apiFetch<ChatSendDto>(`/api/chat/${encodeURIComponent(agentId)}/command`, {
    method: 'POST',
    json: { sessionId, cmd, args },
  })
}

export function cancelChatRun(agentId: string, sessionId: string): Promise<ChatSessionDto> {
  return apiFetch<ChatSessionDto>(`/api/chat/${encodeURIComponent(agentId)}/cancel`, {
    method: 'POST',
    json: { sessionId },
  })
}

export function chatStreamUrl(agentId: string, sessionId: string, runId: string): string {
  const params = new URLSearchParams({ sessionId, runId })
  return `/api/chat/${encodeURIComponent(agentId)}/stream?${params.toString()}`
}
